/**
 * Lookback-day math shared by both products' connector/workflow setup forms.
 *
 * Moved from business/shared/lookbackPeriod.js (Phase 5a) -- dataeng's
 * AddConnector/EditConnector/useConnectorForm need this without reaching
 * into a mantis-owned file. business/shared/lookbackPeriod.js now re-exports
 * from here so its existing mantis-side importers are unaffected.
 */
import {
  formatIsoDate,
  parseIsoDateParts,
} from "./calendarUtils";

export const DEFAULT_LOOKBACK_DAYS = 7;
export const DEFAULT_LOOKBACK_QUICK_PRESETS = [1, 3, 7, 14];
export const DEFAULT_WORKFLOW_LOOKBACK_QUICK_PRESETS = [1, 3, 7, 14];
export const DEFAULT_MIN_LOOKBACK_DAYS = 1;

/**
 * Normalize lookback days to a positive integer, optionally capped by maxDays.
 *
 * @example
 * clampLookbackDays(500);
 * // 500
 *
 * @example
 * clampLookbackDays(0);
 * // 1
 */
export const clampLookbackDays = (
  value,
  { minDays = DEFAULT_MIN_LOOKBACK_DAYS, maxDays } = {}
) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return minDays;
  const rounded = Math.max(minDays, Math.round(parsed));
  if (Number.isFinite(maxDays)) {
    return Math.min(maxDays, rounded);
  }
  return rounded;
};

/**
 * Human-readable summary for a lookback day count.
 *
 * @example
 * formatLookbackDaysLabel(1);
 * // "Last 1 day"
 */
export const formatLookbackDaysLabel = (value) => {
  const days = clampLookbackDays(value);
  if (days === 1) return "Last 1 day";
  return `Last ${days} days`;
};

/**
 * Short lookback range for the simple connector footer (e.g. "30 days").
 *
 * @example
 * formatSimpleLookbackRangeLabel(30);
 * // "30 days"
 */
export const formatSimpleLookbackRangeLabel = (value) => {
  const days = clampLookbackDays(value);
  if (days === 1) return "1 day";
  return `${days} days`;
};

export const TRIAL_LOOKBACK_LOCKED_REASON =
  "Free trial imports the last 7 days, once. Upgrade for up to 12 months and daily sync.";

/**
 * Normalize lookback config returned by `/connectors/lookback-options`.
 * Quick presets are convenience options only. When the payload includes
 * ``max_days`` and locked lookback_options (chat trial), those are kept so
 * the wizard can show locked 14/30/90/365 chips instead of hiding them.
 *
 * @example
 * normalizeLookbackConfig({
 *   quick_presets: [1, 3, 7, 14],
 *   workflow_quick_presets: [1, 3, 7, 14],
 * });
 * // { quickPresets: [1, 3, 7, 14], workflowQuickPresets: [1, 3, 7, 14], minDays: 1 }
 *
 * @example
 * normalizeLookbackConfig({
 *   lookback_options: [
 *     { value: 7, locked: false },
 *     { value: 14, locked: true, locked_reason: "Free trial imports the last 7 days, once." },
 *   ],
 *   max_days: 7,
 *   plan_code: "trial",
 * });
 * // { maxDays: 7, lockedPresets: [{ value: 14, lockedReason: "Free trial..." }], planCode: "trial" }
 */
export const normalizeLookbackConfig = (payload = {}) => {
  const lookbackOptions = Array.isArray(payload.lookback_options)
    ? payload.lookback_options
    : [];

  const quickPresets = Array.isArray(payload.quick_presets)
    ? payload.quick_presets
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0)
    : lookbackOptions
        .map((option) => Number(option?.value))
        .filter((value) => Number.isFinite(value) && value > 0);

  const presets = (quickPresets.length ? quickPresets : DEFAULT_LOOKBACK_QUICK_PRESETS)
    .map((value) => clampLookbackDays(value, { minDays: DEFAULT_MIN_LOOKBACK_DAYS }))
    .filter((value, index, list) => list.indexOf(value) === index)
    .sort((a, b) => a - b);

  const workflowQuickPresets = Array.isArray(payload.workflow_quick_presets)
    ? payload.workflow_quick_presets
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0)
    : [];

  const workflowPresets = (
    workflowQuickPresets.length ? workflowQuickPresets : DEFAULT_WORKFLOW_LOOKBACK_QUICK_PRESETS
  )
    .map((value) => clampLookbackDays(value, { minDays: DEFAULT_MIN_LOOKBACK_DAYS }))
    .filter((value, index, list) => list.indexOf(value) === index)
    .sort((a, b) => a - b);

  const lockedPresets = lookbackOptions
    .filter((option) => option && option.locked)
    .map((option) => {
      const value = Number(option.value);
      return {
        value,
        lockedReason: option.locked_reason || TRIAL_LOOKBACK_LOCKED_REASON,
        planCode: option.plan_code || null,
      };
    })
    .filter((option) => Number.isFinite(option.value) && option.value > 0);

  const allFromOptions = lookbackOptions
    .map((option) => Number(option?.value))
    .filter((value) => Number.isFinite(value) && value > 0);
  const allPresets = (
    allFromOptions.length
      ? allFromOptions
      : presets.concat(lockedPresets.map((option) => option.value))
  )
    .filter((value, index, list) => list.indexOf(value) === index)
    .sort((a, b) => a - b);

  const parsedMax = Number(payload.max_days);
  const maxDays = Number.isFinite(parsedMax) && parsedMax > 0 ? parsedMax : undefined;

  return {
    quickPresets: presets,
    workflowQuickPresets: workflowPresets,
    allPresets: allPresets.length ? allPresets : presets,
    lockedPresets,
    minDays: DEFAULT_MIN_LOOKBACK_DAYS,
    maxDays,
    planCode: payload.plan_code || null,
    upgradeUrl: payload.upgrade_url || "",
  };
};

/**
 * Convert a sync-from date into inclusive lookback days ending today.
 *
 * @example
 * computeLookbackDaysFromStartDate("2026-06-10", new Date("2026-06-15T12:00:00Z"));
 * // 6
 */
export const computeLookbackDaysFromStartDate = (
  isoDate,
  today = new Date(),
  { minDays = DEFAULT_MIN_LOOKBACK_DAYS } = {}
) => {
  const startParts = parseIsoDateParts(isoDate);
  if (!startParts) return null;

  const startDate = new Date(startParts.year, startParts.monthIndex, startParts.day);
  const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (startDate > endDate) return null;

  const diffMs = endDate.getTime() - startDate.getTime();
  const days = Math.floor(diffMs / (24 * 60 * 60 * 1000)) + 1;
  if (days < minDays) return null;
  return days;
};

/**
 * Convert lookback days into the inclusive sync-from date.
 *
 * @example
 * computeStartDateFromLookbackDays(7, new Date("2026-06-15T12:00:00Z"));
 * // "2026-06-09"
 */
export const computeStartDateFromLookbackDays = (
  lookbackDays,
  today = new Date(),
  { minDays = DEFAULT_MIN_LOOKBACK_DAYS } = {}
) => {
  const days = clampLookbackDays(lookbackDays, { minDays });
  const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - (days - 1));
  return formatIsoDate(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
};

/**
 * Format a sync-from date for display.
 *
 * @example
 * formatLookbackStartDateLabel("2026-06-09");
 * // "Jun 9, 2026"
 */
export const formatLookbackStartDateLabel = (isoDate) => {
  const parts = parseIsoDateParts(isoDate);
  if (!parts) return "";
  const date = new Date(parts.year, parts.monthIndex, parts.day);
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};
